import { Stack, Typography } from "@mui/material";
import finance from "financejs";
import { millisToDays, toSpanishDate } from "../../../helpers/dateFormat";
import {
  numberFormat,
  participationsFormat,
} from "../../../helpers/numberFormat";
import { transformInvestments } from "../../../helpers/finance";

export const columnsByStartup = (t) => {
  return [
    {
      field: "alias",
      headerName: t("positions.startup"),
      minWidth: 170,
      flex: 1,
      align: "center",
      headerAlign: "center",
      valueGetter: ({ row }) => row.startup,
    },
    {
      field: "amount",
      headerName: t("positions.amount"),
      minWidth: 100,
      flex: 1,
      align: "center",
      headerAlign: "center",
      valueGetter: ({ row }) => {
        const amount = row.data
          .map((invest) => invest.amount)
          .reduce((a, b) => a + b, 0);
        return numberFormat(amount);
      },
    },
    {
      field: "capitalGain",
      headerName: t("positions.capitalGain"),
      minWidth: 100,
      flex: 1,
      align: "center",
      headerAlign: "center",
      valueGetter: ({ row }) => {
        const res = row.data.map((invest) => {
          const sharesAmount = invest.participationValuation
            ? invest.amount / invest.participationValuation
            : 0;
          const newCapital = sharesAmount * invest.newAmount;
          const resp = invest.participationValuation
            ? newCapital - invest.amount
            : 0;
          return invest.investmentType === "Préstamo Convertible" ? 0 : resp;
        });
        return numberFormat(res.reduce((a, b) => a + b, 0));
      },
    },
    {
      field: "dividends",
      headerName: t("positions.dividends"),
      minWidth: 100,
      flex: 1,
      align: "center",
      headerAlign: "center",
      valueGetter: ({ row }) => {
        const dividendsSum = row.data
          .map((invest) => invest.dividends || [])
          .reduce((acc, currentVal) => acc.concat(currentVal), [])
          .map((div) => div.amount)
          .reduce((a, b) => a + b, 0);
        return numberFormat(dividendsSum);
      },
    },
    {
      field: "tir",
      headerName: t("positions.tir"),
      minWidth: 100,
      flex: 1,
      align: "center",
      headerAlign: "center",
      valueGetter: ({ row }) => {
        const cashflows = [];
        const today = new Date().toISOString().split("T")[0];

        for (const inv of row.data) {
          // 1. Initial outflow (negative)
          cashflows.push({
            cashflow: -inv.amount,
            date: new Date(inv.investmentDate)
          });

          // 2. Dividends (positive)
          if (inv.dividends && inv.dividends.length > 0) {
            for (const div of inv.dividends) {
              cashflows.push({
                cashflow: div.amount,
                date: new Date(div.dividendDate)
              });
            }
          }

          // 3. Terminal value (positive)
          const sharesAmount = inv.participationValuation
            ? inv.amount / inv.participationValuation
            : 0;
          const newCapital = sharesAmount * inv.newAmount;
          const finalValue = inv.investmentType === "Préstamo Convertible" ? inv.amount : (newCapital || inv.amount);
          
          cashflows.push({
            cashflow: finalValue,
            date: new Date(today)
          });
        }

        // Group cashflows by date
        const groupedCashflows = {};
        for (const flow of cashflows) {
          const dateStr = flow.date.toISOString().split("T")[0];
          if (!groupedCashflows[dateStr]) {
            groupedCashflows[dateStr] = 0;
          }
          groupedCashflows[dateStr] += flow.cashflow;
        }

        const finalCashflowList = Object.keys(groupedCashflows).map(dateStr => {
          return {
            cashflow: groupedCashflows[dateStr],
            date: new Date(dateStr)
          };
        });

        const financeInstance = new finance();
        try {
          const tir = financeInstance.XIRR(
            finalCashflowList.map((f) => f.cashflow),
            finalCashflowList.map((f) => f.date),
            -0.25
          );
          return isNaN(tir) || tir === Infinity
            ? "0,00%"
            : tir.toLocaleString("es-ES", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }) + "%";
        } catch (error) {
          console.error("Error al calcular la TIR (XIRR) en columnsByStartup:", error);
          return "-";
        }
      },
    },
    {
      field: "moic",
      headerName: t("positions.moic"),
      minWidth: 100,
      flex: 1,
      align: "center",
      headerAlign: "center",
      valueGetter: ({ row }) => {
        let totalM2M = 0;
        let totalAmount = 0;
        let totalDividends = 0;

        for (const invest of row.data) {
          totalAmount += invest.amount;
          const sharesAmount = invest.participationValuation
            ? invest.amount / invest.participationValuation
            : 0;
          const newCapital = sharesAmount * invest.newAmount;
          const m2mVal = invest.participationValuation
            ? newCapital
            : invest.amount;
          
          totalM2M += (invest.investmentType === "Préstamo Convertible" ? invest.amount : m2mVal);

          if (invest.dividends && invest.dividends.length > 0) {
            const divSum = invest.dividends.reduce((acc, d) => acc + d.amount, 0);
            totalDividends += divSum;
          }
        }

        const moic = (totalM2M + totalDividends) / totalAmount;

        return isNaN(moic) || moic === Infinity
          ? "1,00 x"
          : numberFormat(moic).slice(0, -1) + " x";
      },
    },
    {
      field: "url",
      headerName: t("positions.url"),
      minWidth: 160,
      flex: 1,
      align: "center",
      headerAlign: "center",
      renderCell: ({ row }) => {
        return (
          <Stack direction="row" gap={0}>
            <a href={row.data[0].url} target="_blank" rel="noreferrer">
              {t("link.link")}
            </a>
          </Stack>
        );
      },
    },
  ];
};

export const columnsDetail = (t) => {
  return [
    {
      field: "investmentDate",
      headerName: t("positions.investmentDate"),
      minWidth: 140,
      flex: 1,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Typography color="primary" variant="body2">
          {toSpanishDate(params.row.investmentDate)}
        </Typography>
      ),
    },
    {
      field: "roundNumber",
      headerName: t("positions.roundNumber"),
      minWidth: 110,
      flex: 1,
      align: "center",
      headerAlign: "center",
      valueGetter: ({ row }) => {
        return row.rowType === "dividend" ? "-" : (row.roundNumber || "-");
      }
    },
    {
      field: "amount",
      headerName: t("positions.amount"),
      minWidth: 100,
      flex: 1,
      align: "center",
      headerAlign: "center",
      valueGetter: ({ row }) => {
        return row.rowType === "dividend" || row.amount === null || row.amount === undefined
          ? "-"
          : numberFormat(row.amount.toFixed(2));
      }
    },
    {
      field: "participationValuation",
      headerName: t("positions.participationValuation"),
      minWidth: 130,
      flex: 1,
      align: "center",
      headerAlign: "center",
      valueGetter: ({ row }) => {
        const resp = row.participationValuation;
        return row.rowType === "dividend" || resp === null || row.investmentType === "Préstamo Convertible"
          ? "-"
          : numberFormat(resp.toFixed(2));
      },
    },
    {
      field: "shares",
      headerName: t("positions.shares"),
      minWidth: 120,
      flex: 1,
      align: "center",
      headerAlign: "center",
      valueGetter: ({ row }) => {
        if (row.rowType === "dividend") {
          return participationsFormat(row.sharesAmount.toFixed(2));
        }
        const resp = row.amount / row.participationValuation;
        return row.participationValuation === null ||
          resp === Infinity ||
          row.investmentType === "Préstamo Convertible"
          ? "-"
          : participationsFormat(resp.toFixed(2));
      },
    },
    {
      field: "newAmount",
      headerName: t("positions.newAmount"),
      minWidth: 140,
      flex: 1,
      align: "center",
      headerAlign: "center",
      valueGetter: ({ row }) => {
        if (row.rowType === "dividend") {
          return numberFormat(row.dividendPerShare);
        }
        const resp = row.newAmount;
        return row.participationValuation === null ||
          row.investmentType === "Préstamo Convertible"
          ? "-"
          : numberFormat(resp);
      },
    },
    {
      field: "capitalGain",
      headerName: t("positions.capitalGain"),
      minWidth: 100,
      flex: 1,
      align: "center",
      headerAlign: "center",
      valueGetter: ({ row }) => {
        if (row.rowType === "dividend") {
          return "-";
        }
        const sharesAmount = row.amount / row.participationValuation;
        const newCapital = sharesAmount * row.newAmount;
        const resp = newCapital - row.amount;
        return row.participationValuation === null ||
          row.investmentType === "Préstamo Convertible"
          ? "-"
          : numberFormat(resp.toFixed(2));
      },
    },
    {
      field: "dividends",
      headerName: t("positions.dividends"),
      minWidth: 100,
      flex: 1,
      align: "center",
      headerAlign: "center",
      valueGetter: ({ row }) => {
        if (row.rowType === "dividend") {
          return numberFormat(row.amountDividend);
        }
        return "-";
      },
    },
    {
      field: "tir",
      headerName: t("positions.tir"),
      minWidth: 100,
      flex: 1,
      align: "center",
      headerAlign: "center",
      valueGetter: ({ row }) => {
        if (row.rowType === "dividend") {
          return "-";
        }
        const sharesAmount = row.participationValuation
          ? row.amount / row.participationValuation
          : 0;
        const newCapital = sharesAmount * row.newAmount;
        const finalValue = row.investmentType === "Préstamo Convertible" ? row.amount : (newCapital || row.amount);
        const today = new Date().toISOString().split("T")[0];

        const cashflows = [];
        // 1. Initial outflow (negative)
        cashflows.push({
          cashflow: -row.amount,
          date: new Date(row.investmentDate)
        });

        // 2. Dividends (positive)
        if (row.dividends && row.dividends.length > 0) {
          for (const div of row.dividends) {
            cashflows.push({
              cashflow: div.amount,
              date: new Date(div.dividendDate)
            });
          }
        }

        // 3. Terminal value (positive)
        cashflows.push({
          cashflow: finalValue,
          date: new Date(today)
        });

        // Group cashflows by date
        const groupedCashflows = {};
        for (const flow of cashflows) {
          const dateStr = flow.date.toISOString().split("T")[0];
          if (!groupedCashflows[dateStr]) {
            groupedCashflows[dateStr] = 0;
          }
          groupedCashflows[dateStr] += flow.cashflow;
        }

        const finalCashflowList = Object.keys(groupedCashflows).map(dateStr => {
          return {
            cashflow: groupedCashflows[dateStr],
            date: new Date(dateStr)
          };
        });

        const financeInstance = new finance();
        try {
          const tir = financeInstance.XIRR(
            finalCashflowList.map((f) => f.cashflow),
            finalCashflowList.map((f) => f.date),
            -0.25
          );
          return isNaN(tir) || tir === Infinity
            ? "0,00%"
            : tir.toLocaleString("es-ES", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }) + "%";
        } catch (error) {
          console.error("Error al calcular la TIR (XIRR) en columnsDetail:", error);
          return "-";
        }
      },
    },
    {
      field: "moic",
      headerName: t("positions.moic"),
      minWidth: 100,
      flex: 1,
      align: "center",
      headerAlign: "center",
      valueGetter: ({ row }) => {
        if (row.rowType === "dividend") {
          return "-";
        }
        const sharesAmount = row.participationValuation
          ? row.amount / row.participationValuation
          : 0;
        const newCapital = sharesAmount * row.newAmount;
        const m2mVal = row.participationValuation
          ? newCapital
          : row.amount;

        const finalM2M = row.investmentType === "Préstamo Convertible" ? row.amount : m2mVal;

        const dividendsSum = (row.dividends || [])
          .map((div) => div.amount)
          .reduce((a, b) => a + b, 0);

        const moic = (finalM2M + dividendsSum) / row.amount;

        return isNaN(moic) || moic === Infinity
          ? "1,00 x"
          : numberFormat(moic).slice(0, -1) + " x";
      },
    },
    {
      field: "vehicle",
      headerName: t("positions.vehicle"),
      minWidth: 200,
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "whoInvests",
      headerName: t("positions.whoInvests"),
      minWidth: 200,
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
  ];
};

export const columnsLPByStartup = (t, percentage) => {
  return [
    {
      field: "startup",
      headerName: "Startup",
      minWidth: 170,
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "amount",
      headerName: t("lptable.fundCost"),
      minWidth: 120,
      flex: 1,
      align: "center",
      headerAlign: "center",
      valueGetter: ({ row }) => {
        if (row?.amount) return row.amount;
        return numberFormat(row.data.reduce((acc, obj) => acc + obj.amount, 0));
      },
    },
    {
      field: "fundValue",
      headerName: t("lptable.fundValue"),
      minWidth: 180,
      flex: 1,
      align: "center",
      headerAlign: "center",
      valueGetter: ({ row }) => {
        if (row?.fundValue) return row.fundValue;
        const totalValue = row.data.reduce((acc, obj) => {
          const shares = obj.amount / obj.participationValuation;
          const value = (obj.participationValuation && obj.lastValoration !== null && obj.lastValoration !== undefined)
            ? obj.lastValoration * shares
            : obj.amount;
          return acc + value;
        }, 0);
        return totalValue === 0 ? "-" : numberFormat(totalValue.toFixed(2));
      },
    },
    {
      field: "LPCost",
      headerName: t("lptable.LPCost"),
      minWidth: 120,
      flex: 1,
      align: "center",
      headerAlign: "center",
      valueGetter: ({ row }) => {
        if (row?.LPCost) return row.LPCost;
        const value = row.data.reduce((acc, obj) => {
          return acc + obj.amount * (percentage / 100);
        }, 0);
        return numberFormat(value.toFixed(2));
      },
    },
    {
      field: "LPValue",
      headerName: t("lptable.LPValue"),
      minWidth: 160,
      flex: 1,
      align: "center",
      headerAlign: "center",
      valueGetter: ({ row }) => {
        if (row?.LPValue) return row.LPValue;
        const totalValue = row.data.reduce((acc, obj) => {
          const shares =
            (obj.amount * (percentage / 100)) / obj.participationValuation;
          const value = (obj.participationValuation && obj.lastValoration !== null && obj.lastValoration !== undefined)
            ? obj.lastValoration * shares
            : obj.amount * (percentage / 100);
          return acc + value;
        }, 0);
        return totalValue === 0 ? "-" : numberFormat(totalValue.toFixed(2));
      },
    },
    {
      field: "url",
      headerName: t("opportunities.url"),
      minWidth: 100,
      flex: 1,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        if (params?.row.url === "") return "";
        return (
          <Stack direction="row" gap={0}>
            <a href={params.row.url} target="_blank" rel="noreferrer">
              {t("link.link")}
            </a>
          </Stack>
        );
      },
    },
  ];
};

export const totals = (data, percentage) => {
  return data?.map((row) => {
    const amount = row.data.reduce((acc, obj) => acc + obj.amount, 0);
    const fundValue = row.data.reduce((acc, obj) => {
      const shares = obj.amount / obj.participationValuation;
      const value = (obj.participationValuation && obj.lastValoration !== null && obj.lastValoration !== undefined)
        ? obj.lastValoration * shares
        : obj.amount;
      return acc + value;
    }, 0);
    const LPCost = row.data.reduce(
      (acc, obj) => acc + obj.amount * (percentage / 100),
      0
    );
    const LPValue = row.data.reduce((acc, obj) => {
      const shares =
        (obj.amount * (percentage / 100)) / obj.participationValuation;
      const value = (obj.participationValuation && obj.lastValoration !== null && obj.lastValoration !== undefined)
        ? obj.lastValoration * shares
        : obj.amount * (percentage / 100);
      return acc + value;
    }, 0);
    return {
      startup: row.startup,
      amount: amount,
      fundValue: fundValue === 0 ? "-" : fundValue,
      LPCost: LPCost,
      LPValue: LPValue === 0 ? "-" : LPValue,
      url: "",
    };
  });
};
